import type { PublicationContext, ScreenWidthTargetRange } from '~/types';

const AD_DEVICE_TYPE: Record<string, string> = {
  desktop: 'desktop',
  mobile: 'mobil'
};

function getAdDeviceType (): string {
  return AD_DEVICE_TYPE[getDeviceType()] || 'desktop';
}

/*
 * Reads the `showAdUnits` query param (comma-separated ad ids); when present,
 * only listed ids may render. Route is read via useRoute() — safe on server &
 * client (same pattern as isPreviewMode). Query values arrive already
 * percent-decoded from vue-router, so no decodeURI here.
 */
function isAdUnitAllowed (id: string): boolean {
  const showAdUnits = useRoute()?.query?.showAdUnits;
  if (!showAdUnits) {
    return true;
  }
  return String(showAdUnits)
    .split(',')
    .some((allowedId: string) => allowedId.trim() === id);
}

export function shouldShowAd (ad?: any) {
  if (!ad || !ad.enabled) {
    return false;
  }
  if (ad.type === 'ad_group') {
    return ad.childAds.some((childAd: any) => shouldShowAd(childAd));
  }
  if (!isAdUnitAllowed(ad.id)) {
    return false;
  }
  const foundDeviceType = Object.values(AD_DEVICE_TYPE).find((type: string) => ad.id.includes(type));
  return !foundDeviceType || foundDeviceType === getAdDeviceType();
}

export function loadAd (id: string) {
  if (import.meta.client && window?.lwhb?.cmd) {
    window.lwhb.cmd.push(() => {
      const tagName = id.split('_')[0];
      const device = getAdDeviceType();
      window.lwhb.loadAd({
        tagId: id,
        adUnitName: id.replace(`${tagName}_`, `${tagName}_${device}-`).replace(`-${device}`, ''),
        ignoreVisibility: true
      });
    });
  }
}

function extractLWAdIds (ads: any[], additionalFilter?: (ad: any) => boolean): string[] {
  return ads
    .filter((ad: any) => ad.type !== 'readpeak' && shouldShowAd(ad) && (!additionalFilter || additionalFilter(ad)))
    .flatMap((ad: any) => {
      if (ad.type === 'ad_group') {
        return ad.childAds
          ?.filter((childAd: any) => childAd.type === 'ad' && shouldShowAd(childAd))
          ?.map((childAd: any) => childAd.id) || [];
      } else if (ad.type === 'job') {
        return shouldShowAd(ad.jobAd) ? [ad.jobAd.id] : [];
      }
      return [ad.id];
    });
}

function refreshArticleAds (adConfig: any) {
  if (import.meta.client && window?.lwhb) {
    const staticAds = adConfig?.article?.static || {};
    if ('refresh' in window?.lwhb && typeof window.lwhb.refresh === 'function') {
      const shouldRefreshIds = extractLWAdIds(
        Object.values(staticAds),
        (ad: any) => ad.shouldRefreshOnContentChange
      );
      window.lwhb.refresh(shouldRefreshIds, false);
    }

    const inlineAds = adConfig?.article?.inline || [];
    const streamAds = adConfig?.article?.stream || [];
    if ('removeAdUnit' in window?.lwhb && typeof window.lwhb.removeAdUnit === 'function') {
      const shouldNotRefreshIds = extractLWAdIds(
        Object.values(staticAds),
        (ad: any) => !ad.shouldRefreshOnContentChange
      );
      const combinedAdIds = extractLWAdIds([...inlineAds, ...streamAds]);
      const allAdIds = [...shouldNotRefreshIds, ...combinedAdIds];
      allAdIds.forEach((id) => {
        window.lwhb.removeAdUnit(id);
      });
    }
  }
}

export function resetOrRefreshAds (adConfig: any, routeName: string, lastRouteName: string) {
  if (import.meta.client && window?.lwhb) {
    if (routeName !== lastRouteName && 'resetPage' in window?.lwhb && typeof window.lwhb.resetPage === 'function') {
      window.lwhb.resetPage(true);
    } else if (routeName === 'article') {
      refreshArticleAds(adConfig);
    }
  }
}

export function renderTopBannerBeforeBodyText (topBanner: any) {
  return topBanner?.showBeforeBodyTextOnMobile && getDeviceType() === 'mobile';
}

export function getAdMinHeight (ad: any) {
  if (ad?.minHeight && typeof ad?.minHeight === 'object') {
    return ad.minHeight[getDeviceType()] || 1;
  }
  return 1;
}

export function setStickyHeight (value: number) {
  if (import.meta.server) { return; }
  document.documentElement.style.setProperty('--sticky-height', value + 'px');
  // Emit event to notify listener component
  useBusEmitter('sticky-height-event', { stickyHeight: getStickyHeight() });
}

export function getStickyHeight () {
  if (import.meta.server) { return 0; }
  const stickyHeight = document.documentElement.style.getPropertyValue('--sticky-height') || '0px';
  return parseFloat(stickyHeight.replace('px', ''));
}

export function getAdForIndex (ads: any[], targetIndex: number) {
  return ads.find(ad => ad.enabled && ad.index?.[getDeviceType()] === targetIndex);
}

export function getDetectPositionPayload (item: any, action = ['sticky-hide']) {
  if (item?.type === 'ad') {
    return { type: 'liveWrappedAd', key: (item?.id || item?.ad?.id), action };
  } else if (item?.type === 'image' || item?.type === 'articleImage') {
    return { type: item.type, key: item.src, action: ['sticky-hide'] };
  } else {
    return null;
  }
}

export function isAd (type: string) {
  const adTypes = ['readpeak', 'ad', 'ukens_stilling', 'ad_group', 'job', 'embed'];
  return adTypes.includes(type);
}

export function getScreenWidthTargetRange () {
  if (import.meta.server) { return null; }
  const screenWidthTargetRanges: ScreenWidthTargetRange[] = (useRuntimeConfig().public as unknown as PublicationContext).screenWidthTargetRanges || [];
  const width = screen.width;
  const range = screenWidthTargetRanges.find(range => width >= range.min && width <= range.max);
  return range ? `${range.min}-${range.max}` : null;
}

export function getTestAdStatus (): boolean {
  const testAdsCookie = useCookie('DN_TESTADS');
  return !!testAdsCookie.value;
}

export function getAdConfig () {
  const adConfig = useState<Record<string, any> | null>('adConfig', () => null);
  return adConfig.value ?? {};
}

export function getRetargetingSegments (): string[] {
  if (import.meta.server) { return []; }
  try {
    const storedSegments = localStorage.getItem('nhst:retargeting:segments');
    if (!storedSegments) { return []; }
    const parsedSegments: Record<string, number> = JSON.parse(storedSegments);
    return Object.keys(parsedSegments);
  } catch (error) {
    console.error('Error parsing retargeting segments from localStorage:', error);
    return [];
  }
}
